import { useState, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown } from 'lucide-react';
import '../styles/buttonAnimations.css';

export type DarkSelectOption = string | { value: string; label: string };

function optValue(opt: DarkSelectOption): string {
  return typeof opt === 'string' ? opt : opt.value;
}
function optLabel(opt: DarkSelectOption): string {
  return typeof opt === 'string' ? opt : opt.label;
}

// ─── DarkSelect — single-select portal dropdown ──────────────────────────────
export default function DarkSelect({ value, onChange, options, placeholder = 'Select…', icon, minWidth }: {
  value: string;
  onChange: (v: string) => void;
  options: DarkSelectOption[];
  placeholder?: string;
  // Both additive/optional — every existing caller is unaffected (undefined by default).
  // icon: a small leading icon inside the trigger (e.g. Audit Log's user picker). minWidth: sizes
  // the trigger itself for callers that aren't already stretched by a flex/grid parent.
  icon?: ReactNode;
  minWidth?: number;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top?: number; bottom?: number; left: number; width: number }>({ left: 0, width: 0 });

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (
        containerRef.current && !containerRef.current.contains(e.target as Node) &&
        (!dropdownRef.current || !dropdownRef.current.contains(e.target as Node))
      ) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function openDropdown() {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const maxDropdownH = 202; // maxHeight 200 + 2px gap
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < maxDropdownH) {
        setDropdownPos({ bottom: window.innerHeight - rect.top + 2, left: rect.left, width: rect.width });
      } else {
        setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
      }
    }
    setOpen((prev) => !prev);
  }

  const selectedLabel = options.find((opt) => optValue(opt) === value);

  return (
    <div ref={containerRef} style={{ position: 'relative', ...(minWidth ? { minWidth } : {}) }}>
      <div
        className={`darkselect-trigger${open ? ' open' : ''}`}
        onClick={openDropdown}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
          padding: '6px 10px',
          border: '1px solid rgba(255,255,255,0.13)',
          borderRadius: 6,
          background: 'var(--bg-elevated)',
          color: value ? 'var(--text-primary)' : 'var(--text-muted)',
          fontSize: 12,
          height: 34,
          boxSizing: 'border-box',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, overflow: 'hidden' }}>
          {icon}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedLabel ? optLabel(selectedLabel) : (value || placeholder)}</span>
        </span>
        <ChevronDown size={14} className="darkselect-chevron" style={{ opacity: 0.5, flexShrink: 0 }} />
      </div>
      {open && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            ...(dropdownPos.top !== undefined ? { top: dropdownPos.top } : { bottom: dropdownPos.bottom }),
            left: dropdownPos.left,
            minWidth: dropdownPos.width,
            width: 'max-content',
            maxWidth: 280,
            zIndex: 10001,
            background: 'var(--bg-elevated)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 6,
            color: 'var(--text-primary)',
            maxHeight: 200,
            overflowY: 'auto',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
        >
          {placeholder && (
            <div
              onClick={() => { onChange(''); setOpen(false); }}
              style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)', background: 'transparent', whiteSpace: 'nowrap' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              {placeholder}
            </div>
          )}
          {options.map((opt) => {
            const v = optValue(opt);
            return (
              <div
                key={v}
                onClick={() => { onChange(v); setOpen(false); }}
                style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 13, color: value === v ? 'var(--accent-primary)' : 'var(--text-primary)', background: value === v ? 'var(--bg-hover)' : 'transparent', whiteSpace: 'nowrap' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = value === v ? 'var(--bg-hover)' : 'transparent')}
              >
                {optLabel(opt)}
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}
